import type { ChildProcess } from "node:child_process";
import {
  createOpencodeClient,
  type AssistantMessage as OpenCodeAssistantMessage,
  type Event as OpenCodeEvent,
  type FilePartInput as OpenCodeFilePartInput,
  type OpencodeClient,
  type Part as OpenCodePart,
  type TextPartInput as OpenCodeTextPartInput,
} from "@opencode-ai/sdk/v2/client";
import net from "node:net";
import type { Logger } from "pino";
import { z } from "zod";

import type {
  AgentCapabilityFlags,
  AgentClient,
  AgentLaunchContext,
  AgentMode,
  AgentModelDefinition,
  AgentPermissionRequest,
  AgentPermissionResponse,
  AgentPersistenceHandle,
  AgentPromptInput,
  AgentRunOptions,
  AgentRunResult,
  AgentRuntimeInfo,
  AgentSession,
  AgentSessionConfig,
  AgentSlashCommand,
  AgentStreamEvent,
  AgentTimelineItem,
  AgentUsage,
  ListModelsOptions,
  ListModesOptions,
  ListPersistedAgentsOptions,
  McpServerConfig,
  PersistedAgentDescriptor,
  ToolCallDetail,
  ToolCallTimelineItem,
} from "../agent-sdk-types.js";
import {
  applyProviderEnv,
  resolveProviderCommandPrefix,
  type ProviderRuntimeSettings,
} from "../provider-launch-config.js";
import { findExecutable, isCommandAvailable } from "../../../utils/executable.js";
import { spawnProcess } from "../../../utils/spawn.js";
import { mapOpencodeToolCall } from "./opencode/tool-call-mapper.js";
import { scanProjectClaudeSkills } from "./project-claude-skills.js";
import { OpencodeSidechainTracker } from "./opencode/sidechain-tracker.js";
import { composeAppostleSystemPrompt } from "../appostle-system-prompt.js";
import {
  formatDiagnosticStatus,
  formatProviderDiagnostic,
  formatProviderDiagnosticError,
  resolveBinaryVersion,
  toDiagnosticErrorMessage,
} from "./diagnostic-utils.js";
import { renderPromptAttachmentAsText } from "../prompt-attachments.js";

const OPENCODE_CAPABILITIES: AgentCapabilityFlags = {
  supportsStreaming: true,
  supportsSessionPersistence: true,
  supportsDynamicModes: true,
  supportsMcpServers: true,
  supportsReasoningStream: true,
  supportsToolInvocations: true,
};

const DEFAULT_MODES: AgentMode[] = [
  {
    id: "build",
    label: "Build",
    description: "Allows edits and tool execution for implementation work",
  },
  {
    id: "full-access",
    label: "Full Access",
    description: "Auto-approves all tool calls — no permission prompts",
  },
  {
    id: "plan",
    label: "Plan",
    description: "Read-only planning mode that avoids file edits",
  },
];

type OpenCodeAgentConfig = AgentSessionConfig & { provider: "opencode" };
type OpenCodeMessageRole = "user" | "assistant";

type OpenCodeMcpConfig =
  | {
      type: "local";
      command: string[];
      environment?: Record<string, string>;
      enabled?: boolean;
    }
  | {
      type: "remote";
      url: string;
      headers?: Record<string, string>;
      enabled?: boolean;
    };

const MCP_ALREADY_PRESENT_ERROR_TOKENS = ["already", "exists", "connected"] as const;
const OPENCODE_PROVIDER_LIST_TIMEOUT_MS = 30_000;
const OPENCODE_FATAL_RETRY_MESSAGE_TOKENS = [
  "insufficient balance",
  "no resource package",
  "please recharge",
  "invalid api key",
  "unauthorized",
  "authentication",
  "model not found",
  "unknown model",
  "does not exist",
  "unsupported model",
] as const;
const OPENCODE_HEADERS_TIMEOUT_TOKENS = [
  "headers timeout",
  "headers timeout error",
  "headers_timeout",
  "und_err_headers_timeout",
] as const;

const OpencodeToolStateSchema = z
  .object({
    status: z.string().optional(),
    input: z.unknown().optional(),
    output: z.unknown().optional(),
    error: z.unknown().optional(),
    // OpenCode SDK `ToolStateCompleted.attachments?: Array<FilePart>`. For
    // tool-result images (MCP screenshots, Playwright, etc.) each FilePart's
    // `url` is a `data:<mime>;base64,<data>` URL — the image bytes are inline.
    attachments: z.array(z.unknown()).optional(),
  })
  .passthrough();

const OpencodeToolPartBaseSchema = z
  .object({
    tool: z.string().trim().min(1),
    state: OpencodeToolStateSchema.optional(),
  })
  .passthrough();

const OpencodeToolPartWithCallIdSchema = OpencodeToolPartBaseSchema.extend({
  callID: z.string().trim().min(1),
  id: z.string().optional(),
}).transform((part) => ({
  toolName: part.tool,
  callId: part.callID,
  status: part.state?.status,
  input: part.state?.input,
  output: part.state?.output,
  error: part.state?.error,
  attachments: part.state?.attachments,
}));

const OpencodeToolPartWithIdSchema = OpencodeToolPartBaseSchema.extend({
  id: z.string().trim().min(1),
  callID: z.string().optional(),
}).transform((part) => ({
  toolName: part.tool,
  callId: part.id,
  status: part.state?.status,
  input: part.state?.input,
  output: part.state?.output,
  error: part.state?.error,
  attachments: part.state?.attachments,
}));

const OpencodeToolPartWithoutIdSchema = OpencodeToolPartBaseSchema.extend({
  id: z.string().optional(),
  callID: z.string().optional(),
}).transform((part) => ({
  toolName: part.tool,
  callId: undefined,
  status: part.state?.status,
  input: part.state?.input,
  output: part.state?.output,
  error: part.state?.error,
  attachments: part.state?.attachments,
}));

const OpencodeToolPartSchema = z.union([
  OpencodeToolPartWithCallIdSchema,
  OpencodeToolPartWithIdSchema,
  OpencodeToolPartWithoutIdSchema,
]);

const OpencodeToolPartTimelineEnvelopeSchema = OpencodeToolPartSchema.transform((part) => ({
  toolName: part.toolName,
  callId: part.callId,
  status: part.status,
  input: part.input,
  output: part.output,
  error: part.error,
  attachments: part.attachments,
}));

function mapOpencodeToolPartToTimelineItem(
  part: unknown,
  mcpServerIds: readonly string[],
  questionAnswerCache?: Map<string, Record<string, string>>,
): ToolCallTimelineItem | null {
  const envelope = OpencodeToolPartTimelineEnvelopeSchema.safeParse(part);
  if (!envelope.success) {
    return null;
  }
  // For settled `question` tool parts, fold the resolved answers record into
  // the input so the timeline renders the canonical Q→A card (mirrors how
  // Claude's AskUserQuestion carries `input.answers`) instead of a raw JSON
  // dump. Source: live cache from respondToPermission, else OpenCode's own
  // metadata.answers (historical replay).
  let input = envelope.data.input;
  if (envelope.data.toolName === "question") {
    const answers = resolveOpenCodeQuestionAnswers(part, envelope.data.callId, questionAnswerCache);
    if (answers) {
      const baseInput = readOpenCodeRecord(input) ?? {};
      input = { ...baseInput, answers };
    }
  }
  return mapOpencodeToolCall({
    toolName: envelope.data.toolName,
    callId: envelope.data.callId,
    status: envelope.data.status,
    input,
    output: envelope.data.output,
    error: envelope.data.error,
    attachments: envelope.data.attachments,
    mcpServerIds,
  });
}

const OPENCODE_TASK_FAILED_STATUSES = new Set(["error", "failed", "failure"]);
const OPENCODE_TASK_CANCELED_STATUSES = new Set([
  "canceled",
  "cancelled",
  "aborted",
  "interrupted",
]);
const OPENCODE_TASK_COMPLETED_STATUSES = new Set([
  "complete",
  "completed",
  "success",
  "succeeded",
  "done",
]);

/**
 * Map an OpenCode `task` tool-part `state.status` to the sub_agent emit status.
 * Mirrors the normalisation in `tool-call-mapper.ts` so the SubAgentCard's
 * top-level status tracks the task tool's own lifecycle.
 */
function mapOpencodeTaskStatus(status: unknown): "running" | "completed" | "failed" | "canceled" {
  if (typeof status !== "string") {
    return "running";
  }
  const normalized = status.trim().toLowerCase();
  if (OPENCODE_TASK_FAILED_STATUSES.has(normalized)) {
    return "failed";
  }
  if (OPENCODE_TASK_CANCELED_STATUSES.has(normalized)) {
    return "canceled";
  }
  if (OPENCODE_TASK_COMPLETED_STATUSES.has(normalized)) {
    return "completed";
  }
  return "running";
}

async function resolveOpenCodeBinary(): Promise<string> {
  const found = await findExecutable("opencode");
  if (found) {
    return found;
  }
  throw new Error(
    "OpenCode binary not found. Install OpenCode (https://github.com/opencode-ai/opencode) and ensure it is available in your shell PATH.",
  );
}

function toOpenCodeMcpConfig(config: McpServerConfig, enabled = true): OpenCodeMcpConfig | null {
  if (config.type === "stdio") {
    return {
      type: "local",
      command: [config.command, ...(config.args ?? [])],
      ...(config.env ? { environment: config.env } : {}),
      enabled,
    };
  }

  if (config.type === "http" || config.type === "sse") {
    return {
      type: "remote",
      url: config.url,
      ...(config.headers ? { headers: config.headers } : {}),
      enabled,
    };
  }

  // config.type === "sdk" — in-process SDK MCP servers are Claude-only.
  // OpenCode transport cannot host them; skip silently.
  return null;
}

function stringifyUnknownError(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function normalizeTurnFailureError(error: unknown): string {
  const normalized = stringifyUnknownError(error).trim();
  return normalized.length > 0 ? normalized : "Unknown error";
}

function isOpenCodeNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: unknown }).name === "NotFoundError"
  );
}

async function reconcileOpenCodeSessionClose(params: {
  client: Pick<OpencodeClient, "session">;
  sessionId: string;
  directory: string;
  logger: Logger;
}): Promise<void> {
  const { client, sessionId, directory, logger } = params;

  try {
    const response = await client.session.abort({
      sessionID: sessionId,
      directory,
    });
    if (response.error && !isOpenCodeNotFoundError(response.error)) {
      logger.warn(
        {
          sessionId,
          error: normalizeTurnFailureError(response.error),
        },
        "Failed to abort OpenCode session during close",
      );
    }
  } catch (error) {
    logger.warn(
      {
        sessionId,
        error: normalizeTurnFailureError(error),
      },
      "Failed to abort OpenCode session during close",
    );
  }

  try {
    const response = await client.session.update({
      sessionID: sessionId,
      directory,
      time: { archived: Date.now() },
    });
    if (response.error && !isOpenCodeNotFoundError(response.error)) {
      logger.warn(
        {
          sessionId,
          error: normalizeTurnFailureError(response.error),
        },
        "Failed to archive OpenCode session during close",
      );
    }
  } catch (error) {
    logger.warn(
      {
        sessionId,
        error: normalizeTurnFailureError(error),
      },
      "Failed to archive OpenCode session during close",
    );
  }
}

function isFatalOpenCodeRetryMessage(message: string | null | undefined): boolean {
  const normalized = typeof message === "string" ? message.trim().toLowerCase() : "";
  if (!normalized) {
    return false;
  }
  return OPENCODE_FATAL_RETRY_MESSAGE_TOKENS.some((token) => normalized.includes(token));
}

function isOpenCodeHeadersTimeoutFailure(error: unknown): boolean {
  const diagnostics = new Set<string>();
  const queue: unknown[] = [error];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    const normalized = stringifyUnknownError(current).trim().toLowerCase();
    if (normalized) {
      diagnostics.add(normalized);
    }

    if (typeof current === "object") {
      const record = current as {
        message?: unknown;
        code?: unknown;
        name?: unknown;
        cause?: unknown;
      };

      for (const value of [record.message, record.code, record.name]) {
        if (typeof value === "string") {
          const diagnostic = value.trim().toLowerCase();
          if (diagnostic) {
            diagnostics.add(diagnostic);
          }
        }
      }

      if (record.cause) {
        queue.push(record.cause);
      }
    }
  }

  return [...diagnostics].some((diagnostic) =>
    OPENCODE_HEADERS_TIMEOUT_TOKENS.some((token) => diagnostic.includes(token)),
  );
}

function isAlreadyPresentMcpError(error: unknown): boolean {
  const normalized = stringifyUnknownError(error).toLowerCase();
  return MCP_ALREADY_PRESENT_ERROR_TOKENS.some((token) => normalized.includes(token));
}

async function findAvailablePort(): Promise<number> {
  return new Promise((resolve, reject) => {
    const server = net.createServer();
    server.listen(0, () => {
      const address = server.address();
      if (address && typeof address === "object") {
        const port = address.port;
        server.close(() => resolve(port));
      } else {
        server.close(() => reject(new Error("Failed to get port")));
      }
    });
    server.on("error", reject);
  });
}

function resolvePartDedupeKey(
  part: { id: string; messageID: string },
  partType: "text" | "reasoning",
): string | null {
  if (part.id.trim().length > 0) {
    return `${partType}:${part.id}`;
  }
  if (part.messageID.trim().length > 0) {
    return `${partType}:message:${part.messageID}`;
  }
  return null;
}

function normalizeOpenCodeModeId(modeId: string | null | undefined): string {
  const trimmed = typeof modeId === "string" ? modeId.trim() : "";
  if (!trimmed || trimmed === "default") {
    return "build";
  }
  return trimmed;
}

function sortOpenCodeModes(modes: AgentMode[]): AgentMode[] {
  const order = new Map(DEFAULT_MODES.map((mode, index) => [mode.id, index]));
  return [...modes].sort((left, right) => {
    const leftOrder = order.get(left.id) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = order.get(right.id) ?? Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.label.localeCompare(right.label);
  });
}

function readPositiveFiniteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

function buildOpenCodeModelLookupKey(providerId: string, modelId: string): string {
  return `${providerId}/${modelId}`;
}

function parseOpenCodeModelLookupKey(modelId: string | null | undefined): string | undefined {
  if (typeof modelId !== "string" || modelId.trim().length === 0) {
    return undefined;
  }

  const slashIndex = modelId.indexOf("/");
  if (slashIndex <= 0 || slashIndex === modelId.length - 1) {
    return undefined;
  }

  const providerId = modelId.slice(0, slashIndex).trim();
  const providerModelId = modelId.slice(slashIndex + 1).trim();
  if (!providerId || !providerModelId) {
    return undefined;
  }

  return buildOpenCodeModelLookupKey(providerId, providerModelId);
}

function extractOpenCodeModelContextWindow(model: unknown): number | undefined {
  if (!model || typeof model !== "object") {
    return undefined;
  }
  const limit = (model as { limit?: { context?: unknown } }).limit;
  return readPositiveFiniteNumber(limit?.context);
}

function buildOpenCodeModelDefinition(
  provider: {
    id: string;
    name: string;
  },
  modelId: string,
  model: {
    name: string;
    family?: string;
    release_date?: string;
    attachment?: boolean;
    reasoning?: boolean;
    tool_call?: boolean;
    cost?: unknown;
    limit?: { context?: number; input?: number; output?: number };
    variants?: Record<string, unknown>;
  },
): AgentModelDefinition {
  const rawVariants = model.variants ? Object.keys(model.variants) : [];
  const thinkingOptions = rawVariants.map((id, index) => ({
    id,
    label: id,
    isDefault: index === 0,
  }));

  return {
    provider: "opencode",
    id: `${provider.id}/${modelId}`,
    label: model.name,
    description: `${provider.name} - ${model.family ?? ""}`.trim(),
    thinkingOptions: thinkingOptions.length > 0 ? thinkingOptions : undefined,
    defaultThinkingOptionId: thinkingOptions[0]?.id,
    metadata: {
      providerId: provider.id,
      providerName: provider.name,
      modelId,
      family: model.family,
      releaseDate: model.release_date,
      supportsAttachments: model.attachment,
      supportsReasoning: model.reasoning,
      supportsToolCall: model.tool_call,
      cost: model.cost,
      contextWindowMaxTokens: extractOpenCodeModelContextWindow(model),
      ...(model.limit ? { limit: model.limit } : {}),
    },
  };
}

function resolveOpenCodeSelectedModelContextWindow(
  providers:
    | {
        connected?: string[];
        all?: Array<{
          id: string;
          models?: Record<string, unknown>;
        }>;
      }
    | null
    | undefined,
  modelId: string | null | undefined,
): number | undefined {
  if (!providers) {
    return undefined;
  }
  const modelLookupKey = parseOpenCodeModelLookupKey(modelId);
  if (!modelLookupKey) {
    return undefined;
  }
  const lookup = buildOpenCodeModelContextWindowLookup(providers);
  return lookup.get(modelLookupKey);
}

function buildOpenCodeModelContextWindowLookup(
  providers:
    | {
        connected?: string[];
        all?: Array<{
          id: string;
          models?: Record<string, unknown>;
        }>;
      }
    | null
    | undefined,
): Map<string, number> {
  const lookup = new Map<string, number>();
  if (!providers) {
    return lookup;
  }

  const connectedProviderIds = new Set(providers.connected ?? []);
  for (const provider of providers.all ?? []) {
    if (!connectedProviderIds.has(provider.id)) {
      continue;
    }
    for (const [modelId, modelDefinition] of Object.entries(provider.models ?? {})) {
      const contextWindow = extractOpenCodeModelContextWindow(modelDefinition);
      if (contextWindow === undefined) {
        continue;
      }
      lookup.set(buildOpenCodeModelLookupKey(provider.id, modelId), contextWindow);
    }
  }

  return lookup;
}

function resolveOpenCodeModelLookupKeyFromAssistantMessage(
  info: OpenCodeAssistantMessage,
): string | undefined {
  const providerId = info.providerID;
  const modelId = info.modelID;
  if (!providerId || !modelId) {
    return undefined;
  }

  return buildOpenCodeModelLookupKey(providerId, modelId);
}

function mergeOpenCodeStepFinishUsage(
  usage: AgentUsage,
  part: {
    cost?: unknown;
    tokens?: {
      input?: unknown;
      output?: unknown;
      reasoning?: unknown;
      total?: unknown;
      cache?: {
        read?: unknown;
        write?: unknown;
      };
    };
  },
): void {
  const inputTokens = readPositiveFiniteNumber(part.tokens?.input);
  const outputTokens = readPositiveFiniteNumber(part.tokens?.output);
  const reasoningTokens = readPositiveFiniteNumber(part.tokens?.reasoning);
  const cacheReadTokens = readPositiveFiniteNumber(part.tokens?.cache?.read);
  const cacheWriteTokens = readPositiveFiniteNumber(part.tokens?.cache?.write);
  const totalTokens =
    (inputTokens ?? 0) +
    (outputTokens ?? 0) +
    (reasoningTokens ?? 0) +
    (cacheReadTokens ?? 0) +
    (cacheWriteTokens ?? 0);
  const cost = readPositiveFiniteNumber(part.cost);

  if (inputTokens !== undefined) {
    usage.inputTokens = inputTokens;
  }
  if (cacheReadTokens !== undefined) {
    usage.cachedInputTokens = cacheReadTokens;
  }
  if (outputTokens !== undefined) {
    usage.outputTokens = outputTokens;
  }
  if (totalTokens > 0) {
    usage.contextWindowUsedTokens = totalTokens;
  }
  if (cost !== undefined) {
    usage.totalCostUsd = (usage.totalCostUsd ?? 0) + cost;
  }
}

function hasNormalizedOpenCodeUsage(usage: AgentUsage): boolean {
  return [
    usage.inputTokens,
    usage.cachedInputTokens,
    usage.outputTokens,
    usage.totalCostUsd,
    usage.contextWindowMaxTokens,
    usage.contextWindowUsedTokens,
  ].some((value) => typeof value === "number" && Number.isFinite(value));
}

function getOpenCodeAttachmentExtension(mimeType: string): string {
  switch (mimeType) {
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    case "image/webp":
      return "webp";
    case "image/gif":
      return "gif";
    case "image/svg+xml":
      return "svg";
    default:
      return "bin";
  }
}

function toOpenCodeDataUrl(mimeType: string, data: string): { mimeType: string; url: string } {
  const match = data.match(/^data:([^;,]+);base64,(.+)$/);
  if (match) {
    return {
      mimeType: match[1] ?? mimeType,
      url: data,
    };
  }
  return {
    mimeType,
    url: `data:${mimeType};base64,${data}`,
  };
}

function buildOpenCodePromptParts(
  prompt: AgentPromptInput,
): Array<OpenCodeTextPartInput | OpenCodeFilePartInput> {
  if (typeof prompt === "string") {
    return [{ type: "text", text: prompt }];
  }
  let attachmentOrdinal = 0;
  const output: Array<OpenCodeTextPartInput | OpenCodeFilePartInput> = [];
  for (const part of prompt) {
    if (part.type === "text") {
      output.push({ type: "text", text: part.text });
      continue;
    }
    if (
      part.type === "github_pr" ||
      part.type === "github_issue" ||
      part.type === "gitlab_mr" ||
      part.type === "gitlab_issue"
    ) {
      output.push({ type: "text", text: renderPromptAttachmentAsText(part) });
      continue;
    }
    attachmentOrdinal += 1;
    const normalized = toOpenCodeDataUrl(part.mimeType, part.data);
    output.push({
      type: "file",
      mime: normalized.mimeType,
      filename: `attachment-${attachmentOrdinal}.${getOpenCodeAttachmentExtension(
        normalized.mimeType,
      )}`,
      url: normalized.url,
    });
  }
  return output;
}

export const __openCodeInternals = {
  buildOpenCodePromptParts,
  buildOpenCodeModelContextWindowLookup,
  buildOpenCodeModelDefinition,
  buildOpenCodeModelLookupKey,
  extractOpenCodeModelContextWindow,
  hasNormalizedOpenCodeUsage,
  mergeOpenCodeStepFinishUsage,
  parseOpenCodeModelLookupKey,
  reconcileOpenCodeSessionClose,
  resolveOpenCodeModelLookupKeyFromAssistantMessage,
  resolveOpenCodeSelectedModelContextWindow,
};

export class OpenCodeServerManager {
  private static instance: OpenCodeServerManager | null = null;
  private static exitHandlerRegistered = false;
  private server: ChildProcess | null = null;
  private port: number | null = null;
  private startPromise: Promise<{ port: number; url: string }> | null = null;
  private readonly logger: Logger;
  private readonly runtimeSettings?: ProviderRuntimeSettings;
  private readonly runtimeSettingsKey: string;

  private constructor(logger: Logger, runtimeSettings?: ProviderRuntimeSettings) {
    this.logger = logger;
    this.runtimeSettings = runtimeSettings;
    this.runtimeSettingsKey = JSON.stringify(runtimeSettings ?? {});
  }

  static getInstance(
    logger: Logger,
    runtimeSettings?: ProviderRuntimeSettings,
  ): OpenCodeServerManager {
    const nextSettingsKey = JSON.stringify(runtimeSettings ?? {});
    if (!OpenCodeServerManager.instance) {
      OpenCodeServerManager.instance = new OpenCodeServerManager(logger, runtimeSettings);
      OpenCodeServerManager.registerExitHandler();
    } else if (OpenCodeServerManager.instance.runtimeSettingsKey !== nextSettingsKey) {
      logger.warn(
        {
          existingRuntimeSettings: OpenCodeServerManager.instance.runtimeSettingsKey,
          requestedRuntimeSettings: nextSettingsKey,
        },
        "OpenCode server manager already initialized with different runtime settings",
      );
    }
    return OpenCodeServerManager.instance;
  }

  private static registerExitHandler(): void {
    if (OpenCodeServerManager.exitHandlerRegistered) {
      return;
    }
    OpenCodeServerManager.exitHandlerRegistered = true;

    const cleanup = () => {
      const instance = OpenCodeServerManager.instance;
      if (instance?.server && !instance.server.killed) {
        instance.server.kill("SIGTERM");
      }
    };

    process.on("exit", cleanup);
    process.on("SIGTERM", cleanup);
    process.on("SIGINT", cleanup);
  }

  async ensureRunning(): Promise<{ port: number; url: string }> {
    if (this.startPromise) {
      return this.startPromise;
    }

    if (this.server && this.port && !this.server.killed) {
      return { port: this.port, url: `http://127.0.0.1:${this.port}` };
    }

    this.startPromise = this.startServer();
    try {
      const result = await this.startPromise;
      return result;
    } finally {
      this.startPromise = null;
    }
  }

  private async startServer(): Promise<{ port: number; url: string }> {
    this.port = await findAvailablePort();
    const url = `http://127.0.0.1:${this.port}`;
    const launchPrefix = await resolveProviderCommandPrefix(
      this.runtimeSettings?.command,
      resolveOpenCodeBinary,
    );

    return new Promise((resolve, reject) => {
      this.server = spawnProcess(
        launchPrefix.command,
        [...launchPrefix.args, "serve", "--port", String(this.port)],
        {
          stdio: ["ignore", "pipe", "pipe"],
          env: applyProviderEnv(process.env, this.runtimeSettings),
        },
      );

      let started = false;
      const timeout = setTimeout(() => {
        if (!started) {
          reject(new Error("OpenCode server startup timeout"));
        }
      }, 30_000);

      this.server.stdout?.on("data", (data: Buffer) => {
        const output = data.toString();
        if (output.includes("listening on") && !started) {
          started = true;
          clearTimeout(timeout);
          resolve({ port: this.port!, url });
        }
      });

      this.server.stderr?.on("data", (data: Buffer) => {
        this.logger.error({ stderr: data.toString().trim() }, "OpenCode server stderr");
      });

      this.server.on("error", (error) => {
        clearTimeout(timeout);
        reject(error);
      });

      this.server.on("exit", (code) => {
        if (!started) {
          clearTimeout(timeout);
          reject(new Error(`OpenCode server exited with code ${code}`));
        }
        this.server = null;
        this.port = null;
      });
    });
  }

  async shutdown(): Promise<void> {
    if (this.server && !this.server.killed) {
      this.server.kill("SIGTERM");
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          this.server?.kill("SIGKILL");
          resolve();
        }, 5000);
        this.server?.on("exit", () => {
          clearTimeout(timeout);
          resolve();
        });
      });
    }
    this.server = null;
    this.port = null;
  }
}

export class OpenCodeAgentClient implements AgentClient {
  readonly provider: "opencode" = "opencode";
  readonly capabilities = OPENCODE_CAPABILITIES;

  private readonly serverManager: OpenCodeServerManager;
  private readonly logger: Logger;
  private readonly runtimeSettings?: ProviderRuntimeSettings;
  private readonly modelContextWindows = new Map<string, number>();

  constructor(logger: Logger, runtimeSettings?: ProviderRuntimeSettings) {
    this.logger = logger.child({ module: "agent", provider: "opencode" });
    this.runtimeSettings = runtimeSettings;
    this.serverManager = OpenCodeServerManager.getInstance(this.logger, runtimeSettings);
  }

  async createSession(
    config: AgentSessionConfig,
    _launchContext?: AgentLaunchContext,
  ): Promise<AgentSession> {
    const openCodeConfig = this.assertConfig(config);
    const { url } = await this.serverManager.ensureRunning();
    const client = createOpencodeClient({
      baseUrl: url,
      directory: openCodeConfig.cwd,
    });

    // Set a timeout for session creation to fail fast
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("OpenCode session.create timed out after 10s")), 10_000);
    });

    const response = await Promise.race([
      client.session.create({ directory: openCodeConfig.cwd }),
      timeoutPromise,
    ]);

    if (response.error) {
      throw new Error(`Failed to create OpenCode session: ${JSON.stringify(response.error)}`);
    }

    const session = response.data;
    if (!session) {
      throw new Error("OpenCode session creation returned no data");
    }

    await this.populateModelContextWindowCache(client, openCodeConfig.cwd);

    return new OpenCodeAgentSession(
      openCodeConfig,
      client,
      session.id,
      this.logger,
      new Map(this.modelContextWindows),
    );
  }

  async resumeSession(
    handle: AgentPersistenceHandle,
    overrides?: Partial<AgentSessionConfig>,
    _launchContext?: AgentLaunchContext,
  ): Promise<AgentSession> {
    const cwd = overrides?.cwd ?? (handle.metadata?.cwd as string);
    if (!cwd) {
      throw new Error("OpenCode resume requires the original working directory");
    }

    const config: AgentSessionConfig = {
      provider: "opencode",
      cwd,
      ...overrides,
    };
    const openCodeConfig = this.assertConfig(config);
    const { url } = await this.serverManager.ensureRunning();
    const client = createOpencodeClient({
      baseUrl: url,
      directory: openCodeConfig.cwd,
    });

    await this.populateModelContextWindowCache(client, openCodeConfig.cwd);

    return new OpenCodeAgentSession(
      openCodeConfig,
      client,
      handle.sessionId,
      this.logger,
      new Map(this.modelContextWindows),
    );
  }

  async listModels(options?: ListModelsOptions): Promise<AgentModelDefinition[]> {
    const { url } = await this.serverManager.ensureRunning();
    const client = createOpencodeClient({
      baseUrl: url,
      directory: options?.cwd ?? process.cwd(),
    });

    // Background model discovery can be legitimately slow while OpenCode refreshes
    // provider state, so allow longer than turn execution paths.
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(
        () =>
          reject(
            new Error(
              `OpenCode provider.list timed out after ${OPENCODE_PROVIDER_LIST_TIMEOUT_MS / 1000}s - server may not be authenticated or connected to any providers`,
            ),
          ),
        OPENCODE_PROVIDER_LIST_TIMEOUT_MS,
      );
    });

    const response = await Promise.race([
      client.provider.list({ directory: options?.cwd ?? process.cwd() }),
      timeoutPromise,
    ]);

    if (response.error) {
      throw new Error(`Failed to fetch OpenCode providers: ${JSON.stringify(response.error)}`);
    }

    const providers = response.data;
    if (!providers) {
      return [];
    }

    // Only include models from connected providers (ones that are actually available)
    const connectedProviderIds = new Set(providers.connected);

    // Fail fast if no providers are connected
    if (connectedProviderIds.size === 0) {
      throw new Error(
        "OpenCode has no connected providers. Please authenticate with at least one provider (e.g., openai, anthropic) or set appropriate environment variables (e.g., OPENAI_API_KEY).",
      );
    }

    const models: AgentModelDefinition[] = [];
    this.modelContextWindows.clear();
    for (const provider of providers.all) {
      // Skip providers that aren't connected/configured
      if (!connectedProviderIds.has(provider.id)) {
        continue;
      }

      for (const [modelId, model] of Object.entries(provider.models)) {
        const definition = buildOpenCodeModelDefinition(provider, modelId, model);
        const contextWindowMaxTokens = extractOpenCodeModelContextWindow(model);
        if (contextWindowMaxTokens !== undefined) {
          this.modelContextWindows.set(
            buildOpenCodeModelLookupKey(provider.id, modelId),
            contextWindowMaxTokens,
          );
        }
        models.push(definition);
      }
    }

    return models;
  }

  async listModes(options?: ListModesOptions): Promise<AgentMode[]> {
    const { url } = await this.serverManager.ensureRunning();
    const directory = options?.cwd ?? process.cwd();
    const client = createOpencodeClient({ baseUrl: url, directory });

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("OpenCode app.agents timed out after 10s")), 10_000);
    });

    const response = await Promise.race([client.app.agents({ directory }), timeoutPromise]);

    if (response.error || !response.data) {
      return DEFAULT_MODES;
    }

    const discovered = response.data
      .filter((agent) => agent.mode === "primary" && agent.hidden !== true)
      .map((agent) => ({
        id: agent.name,
        label: agent.name.charAt(0).toUpperCase() + agent.name.slice(1),
        description:
          typeof agent.description === "string" && agent.description.trim().length > 0
            ? agent.description.trim()
            : DEFAULT_MODES.find((mode) => mode.id === agent.name)?.description,
      }));

    const modes = discovered.length > 0 ? sortOpenCodeModes(discovered) : DEFAULT_MODES;
    // full-access is an Appostle-level mode (auto-approves all permissions) — ensure it's always present
    const fullAccess = DEFAULT_MODES.find((m) => m.id === "full-access")!;
    if (!modes.some((m) => m.id === "full-access")) {
      // Insert after the first mode (build) so it's prominent but not default
      modes.splice(1, 0, fullAccess);
    }
    return modes;
  }

  async listPersistedAgents(
    _options?: ListPersistedAgentsOptions,
  ): Promise<PersistedAgentDescriptor[]> {
    // TODO: Implement by listing sessions from OpenCode
    return [];
  }

  async isAvailable(): Promise<boolean> {
    const command = this.runtimeSettings?.command;
    if (command?.mode === "replace") {
      return await isCommandAvailable(command.argv[0]);
    }
    return await isCommandAvailable("opencode");
  }

  async getDiagnostic(): Promise<{ diagnostic: string }> {
    try {
      const available = await this.isAvailable();
      const resolvedBinary = await findExecutable("opencode");
      let serverStatus = "Not running";
      let modelsValue = "Not checked";
      let status = formatDiagnosticStatus(available);

      try {
        const { url } = await this.serverManager.ensureRunning();
        serverStatus = `Running (${url})`;
      } catch (error) {
        serverStatus = `Unavailable (${normalizeTurnFailureError(error)})`;
      }

      if (available) {
        try {
          const models = await this.listModels();
          modelsValue = String(models.length);
        } catch (error) {
          modelsValue = `Error - ${toDiagnosticErrorMessage(error)}`;
          status = formatDiagnosticStatus(available, {
            source: "model fetch",
            cause: error,
          });
        }

        if (!modelsValue.startsWith("Error -")) {
          try {
            await this.listModes();
          } catch (error) {
            status = formatDiagnosticStatus(available, {
              source: "mode fetch",
              cause: error,
            });
          }
        }
      }

      return {
        diagnostic: formatProviderDiagnostic("OpenCode", [
          {
            label: "Binary",
            value: resolvedBinary ?? "not found",
          },
          {
            label: "Version",
            value: resolvedBinary ? await resolveBinaryVersion(resolvedBinary) : "unknown",
          },
          { label: "Server", value: serverStatus },
          { label: "Models", value: modelsValue },
          { label: "Status", value: status },
        ]),
      };
    } catch (error) {
      return {
        diagnostic: formatProviderDiagnosticError("OpenCode", error),
      };
    }
  }
  private assertConfig(config: AgentSessionConfig): OpenCodeAgentConfig {
    if (config.provider !== "opencode") {
      throw new Error(`OpenCodeAgentClient received config for provider '${config.provider}'`);
    }
    return { ...config, provider: "opencode" };
  }

  private async populateModelContextWindowCache(
    client: OpencodeClient,
    cwd: string,
  ): Promise<void> {
    const response = await client.provider.list({ directory: cwd });
    if (response.error || !response.data) {
      return;
    }

    const lookup = buildOpenCodeModelContextWindowLookup(response.data);
    this.modelContextWindows.clear();
    for (const [modelLookupKey, contextWindowMaxTokens] of lookup.entries()) {
      this.modelContextWindows.set(modelLookupKey, contextWindowMaxTokens);
    }
  }
}

export type OpenCodeEventTranslationState = {
  sessionId: string;
  messageRoles: Map<string, OpenCodeMessageRole>;
  accumulatedUsage: AgentUsage;
  streamedPartKeys: Set<string>;
  emittedStructuredMessageIds: Set<string>;
  /** Tracks the type of each part by ID, learned from message.part.updated events. */
  partTypes: Map<string, string>;
  modelContextWindowsByModelKey?: ReadonlyMap<string, number>;
  onAssistantModelContextWindowResolved?: (contextWindowMaxTokens: number) => void;
  /** Known MCP server ids, used to canonicalize OpenCode tool-call names. */
  mcpServerIds?: readonly string[];
  /**
   * Tracks `task` subagent child sessions and folds their tool parts into a live
   * `sub_agent` action log on the parent task tool call. Optional so tests that
   * don't exercise subagents can omit it.
   */
  subAgentTracker?: OpencodeSidechainTracker;
  /**
   * Answers submitted for `question` tool calls in this session, keyed by the
   * tool part's `callID`. Populated by `respondToPermission` on the live path;
   * enables the settled `question` tool_call to render a Q→A card instead of a
   * raw JSON dump.  Optional so unit tests that don't exercise questions can omit it.
   */
  questionAnswerCache?: Map<string, Record<string, string>>;
};

function stringifyStructuredAssistantMessage(value: unknown): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function readOpenCodeRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function mapOpenCodeTodosToTimelineItems(
  todos: Array<{ content?: string | null; status?: string | null }>,
): Extract<AgentTimelineItem, { type: "todo" }> {
  return {
    type: "todo",
    items: todos.flatMap((todo) => {
      const text = readNonEmptyString(todo.content);
      if (!text) {
        return [];
      }

      return [
        {
          text,
          completed: todo.status === "completed",
        },
      ];
    }),
  };
}

function createCompactionTimelineItem(
  status: Extract<AgentTimelineItem, { type: "compaction" }>["status"],
  trigger?: Extract<AgentTimelineItem, { type: "compaction" }>["trigger"],
): Extract<AgentTimelineItem, { type: "compaction" }> {
  return {
    type: "compaction",
    status,
    ...(trigger ? { trigger } : {}),
  };
}

const PERMISSION_COMMAND_KEYS = ["command", "cmd", "shellCommand"] as const;
const PERMISSION_CWD_KEYS = ["cwd", "directory", "path", "workdir"] as const;
const PERMISSION_REASON_KEYS = ["reason", "purpose", "description", "message"] as const;
const PERMISSION_TITLE_BY_NAME: Record<string, string> = {
  external_directory: "Access external directory",
  bash: "Run shell command",
  read: "Read files",
  read_file: "Read files",
  write: "Write files",
  write_file: "Write files",
  create_file: "Write files",
  edit: "Edit files",
  apply_patch: "Edit files",
  apply_diff: "Edit files",
};

function toHumanReadablePermissionTitle(permission: string): string {
  const mapped = PERMISSION_TITLE_BY_NAME[permission];
  if (mapped) {
    return mapped;
  }

  const normalized = permission
    .split(/[\s_-]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
  return normalized.length > 0 ? normalized : "Permission request";
}

function readFirstStringFromRecord(
  record: Record<string, unknown> | null,
  keys: readonly string[],
): string | null {
  if (!record) {
    return null;
  }
  for (const key of keys) {
    const value = readNonEmptyString(record[key]);
    if (value) {
      return value;
    }
  }
  return null;
}

function readPermissionField(
  metadata: Record<string, unknown> | null,
  keys: readonly string[],
): string | null {
  const direct = readFirstStringFromRecord(metadata, keys);
  if (direct) {
    return direct;
  }

  const nestedInput = readOpenCodeRecord(metadata?.input);
  return readFirstStringFromRecord(nestedInput, keys);
}

function buildOpenCodePermissionInput(params: {
  patterns: string[];
  metadata: Record<string, unknown> | null;
  tool: Record<string, unknown> | null;
  command: string | null;
}): Record<string, unknown> {
  return {
    ...(params.patterns.length > 0 ? { patterns: params.patterns } : {}),
    ...(params.metadata ? { metadata: params.metadata } : {}),
    ...(params.tool ? { tool: params.tool } : {}),
    ...(params.command ? { command: params.command } : {}),
  };
}

function buildOpenCodePermissionDetail(params: {
  permission: string;
  input: Record<string, unknown>;
  command: string | null;
  cwd: string | null;
}): ToolCallDetail {
  if (params.command) {
    return {
      type: "shell",
      command: params.command,
      ...(params.cwd ? { cwd: params.cwd } : {}),
    };
  }

  return {
    type: "unknown",
    input: {
      permission: params.permission,
      ...params.input,
    },
    output: null,
  };
}

function buildOpenCodePermissionDescription(params: {
  reason: string | null;
  patterns: string[];
}): string | undefined {
  const parts: string[] = [];
  if (params.reason) {
    parts.push(params.reason);
  }
  if (params.patterns.length > 0) {
    parts.push(`Scope: ${params.patterns.join(", ")}`);
  }
  return parts.length > 0 ? parts.join(" - ") : undefined;
}

/**
 * Build the canonical `answers` record (`{ [question_text]: chosen_labels }`)
 * for a settled OpenCode `question` tool part.
 *
 * Two sources, tried in order:
 * 1. The live `questionAnswerCache` populated by `respondToPermission` — most
 *    accurate because it comes straight from QuestionFormCard.
 * 2. `part.state.metadata.answers` written by OpenCode itself — available for
 *    historical replay (the cache is empty after reconnect/reload).
 *    OpenCode stores answers as `Array<Array<string>>` (per-question arrays of
 *    selected labels); we zip these with the questions array to rebuild the
 *    canonical record.
 */
function resolveOpenCodeQuestionAnswers(
  rawPart: unknown,
  callId: string | null | undefined,
  questionAnswerCache: Map<string, Record<string, string>> | undefined,
): Record<string, string> | null {
  // Source 1: live cache from respondToPermission
  if (callId && questionAnswerCache) {
    const cached = questionAnswerCache.get(callId);
    if (cached) {
      return cached;
    }
  }
  // Source 2: OpenCode's own metadata.answers in the tool state
  const toolState = readOpenCodeRecord(readOpenCodeRecord(rawPart)?.state);
  const metaAnswers = toolState?.metadata;
  const answersArr = Array.isArray(readOpenCodeRecord(metaAnswers)?.answers)
    ? (readOpenCodeRecord(metaAnswers)!.answers as unknown[])
    : null;
  if (!answersArr) {
    return null;
  }
  const questions = Array.isArray(toolState?.input)
    ? null
    : Array.isArray(readOpenCodeRecord(toolState?.input)?.questions)
      ? (readOpenCodeRecord(toolState?.input)!.questions as unknown[])
      : null;
  if (!questions) {
    return null;
  }
  const record: Record<string, string> = {};
  for (let i = 0; i < questions.length; i++) {
    const q = readOpenCodeRecord(questions[i]);
    const questionText = readNonEmptyString(q?.question);
    if (!questionText) {
      continue;
    }
    const selected = Array.isArray(answersArr[i]) ? (answersArr[i] as unknown[]) : [];
    const labels = selected
      .filter((s): s is string => typeof s === "string" && s.length > 0)
      .join(", ");
    if (labels.length > 0) {
      record[questionText] = labels;
    }
  }
  return Object.keys(record).length > 0 ? record : null;
}

export function translateOpenCodeEvent(
  event: OpenCodeEvent,
  state: OpenCodeEventTranslationState,
): AgentStreamEvent[] {
  const events: AgentStreamEvent[] = [];

  switch (event.type) {
    case "session.created":
    case "session.updated": {
      const info = event.properties.info;
      if (info.id === state.sessionId) {
        events.push({
          type: "thread_started",
          sessionId: state.sessionId,
          provider: "opencode",
        });
      } else if (info.parentID === state.sessionId) {
        // A `task` subagent runs in a child session of ours. Register it so its
        // tool parts get folded into the parent task's live action log.
        state.subAgentTracker?.registerChildSession(info.id);
      }
      break;
    }

    case "message.updated": {
      const info = event.properties.info;
      if (info.sessionID !== state.sessionId) {
        break;
      }

      state.messageRoles.set(info.id, info.role);
      if (info.role === "assistant") {
        const modelLookupKey = resolveOpenCodeModelLookupKeyFromAssistantMessage(info);
        if (modelLookupKey) {
          const contextWindowMaxTokens = state.modelContextWindowsByModelKey?.get(modelLookupKey);
          if (contextWindowMaxTokens !== undefined) {
            state.onAssistantModelContextWindowResolved?.(contextWindowMaxTokens);
          }
        }

        if (!state.emittedStructuredMessageIds.has(info.id) && info.time?.completed !== undefined) {
          const text = stringifyStructuredAssistantMessage(info.structured);
          if (text) {
            state.emittedStructuredMessageIds.add(info.id);
            events.push({
              type: "timeline",
              provider: "opencode",
              item: { type: "assistant_message", text },
            });
          }
        }
      }
      break;
    }

    case "message.part.updated": {
      const part = event.properties.part;
      if (part.sessionID !== state.sessionId) {
        // Tool parts emitted on a registered `task` subagent child session are
        // folded into the parent task's live action log. All other foreign-session
        // parts (assistant text, reasoning) are dropped so they never leak into
        // this agent's top-level timeline.
        if (part.type === "tool" && state.subAgentTracker?.isChildSession(part.sessionID)) {
          const toolState = readOpenCodeRecord(part.state);
          events.push(
            ...state.subAgentTracker.handleChildToolPart({
              childSessionId: part.sessionID,
              toolName: part.tool,
              callId: readNonEmptyString(part.callID) ?? readNonEmptyString(part.id) ?? undefined,
              input: toolState?.input,
              mcpServerIds: state.mcpServerIds ?? [],
            }),
          );
        }
        break;
      }

      const messageRole = state.messageRoles.get(part.messageID);
      state.partTypes.set(part.id, part.type);

      if (part.type === "text") {
        const partKey = resolvePartDedupeKey(part, "text");
        if (messageRole === "user") {
          break;
        }
        if (part.time?.end) {
          if (partKey && state.streamedPartKeys.delete(partKey)) {
            break;
          }
          if (part.text) {
            events.push({
              type: "timeline",
              provider: "opencode",
              item: { type: "assistant_message", text: part.text },
            });
          }
        }
      } else if (part.type === "reasoning") {
        const partKey = resolvePartDedupeKey(part, "reasoning");
        if (part.time.end) {
          if (partKey && state.streamedPartKeys.delete(partKey)) {
            break;
          }
          if (part.text) {
            events.push({
              type: "timeline",
              provider: "opencode",
              item: { type: "reasoning", text: part.text },
            });
          }
        }
      } else if (part.type === "tool") {
        const toolItem = mapOpencodeToolPartToTimelineItem(
          part,
          state.mcpServerIds ?? [],
          state.questionAnswerCache,
        );
        if (toolItem) {
          events.push({
            type: "timeline",
            provider: "opencode",
            item: toolItem,
          });
        }
        // The `task` tool spawns a native subagent in a child session. Feed its
        // input/output/metadata to the sidechain tracker so it learns the
        // child-session correlation and emits an enriched `sub_agent` card
        // (subAgentType + description + live action log) on the same callId.
        if (state.subAgentTracker && part.tool === "task") {
          const callId = readNonEmptyString(part.callID) ?? readNonEmptyString(part.id);
          if (callId) {
            const toolState = readOpenCodeRecord(part.state);
            events.push(
              ...state.subAgentTracker.handleTaskPart({
                callId,
                input: toolState?.input,
                output: toolState?.output,
                metadata: toolState?.metadata,
                status: mapOpencodeTaskStatus(toolState?.status),
                mcpServerIds: state.mcpServerIds ?? [],
              }),
            );
          }
        }
      } else if (part.type === "compaction") {
        events.push({
          type: "timeline",
          provider: "opencode",
          item: createCompactionTimelineItem("loading", part.auto ? "auto" : "manual"),
        });
      } else if (part.type === "step-finish") {
        mergeOpenCodeStepFinishUsage(state.accumulatedUsage, part);
        if (hasNormalizedOpenCodeUsage(state.accumulatedUsage)) {
          events.push({
            type: "usage_updated",
            provider: "opencode",
            usage: { ...state.accumulatedUsage },
          });
        }
      }
      break;
    }

    case "message.part.delta": {
      const { sessionID, messageID, partID, field, delta } = event.properties;
      if (sessionID !== state.sessionId) {
        break;
      }

      if (!delta || !field) {
        break;
      }

      const messageRole = messageID ? state.messageRoles.get(messageID) : undefined;
      const knownPartType = partID ? state.partTypes.get(partID) : undefined;
      const isReasoning = knownPartType === "reasoning" || field === "reasoning";

      if (isReasoning) {
        if (partID) {
          state.streamedPartKeys.add(`reasoning:${partID}`);
        }
        events.push({
          type: "timeline",
          provider: "opencode",
          item: { type: "reasoning", text: delta },
        });
      } else if (field === "text") {
        if (messageRole === "user") {
          break;
        }
        if (partID) {
          state.streamedPartKeys.add(`text:${partID}`);
        }
        events.push({
          type: "timeline",
          provider: "opencode",
          item: { type: "assistant_message", text: delta },
        });
      }
      break;
    }

    case "permission.asked": {
      if (event.properties.sessionID !== state.sessionId) {
        break;
      }

      const metadata = readOpenCodeRecord(event.properties.metadata);
      const tool = readOpenCodeRecord(event.properties.tool);
      const patterns = Array.isArray(event.properties.patterns)
        ? event.properties.patterns.filter((value): value is string => typeof value === "string")
        : [];
      const command = readPermissionField(metadata, PERMISSION_COMMAND_KEYS);
      const cwd = readPermissionField(metadata, PERMISSION_CWD_KEYS);
      const reason = readPermissionField(metadata, PERMISSION_REASON_KEYS);
      const input = buildOpenCodePermissionInput({
        patterns,
        metadata,
        tool,
        command,
      });
      const detail = buildOpenCodePermissionDetail({
        permission: event.properties.permission,
        input,
        command,
        cwd,
      });
      const description = buildOpenCodePermissionDescription({
        reason,
        patterns,
      });

      events.push({
        type: "permission_requested",
        provider: "opencode",
        request: {
          id: event.properties.id,
          provider: "opencode",
          name: event.properties.permission,
          kind: "tool",
          title: toHumanReadablePermissionTitle(event.properties.permission),
          ...(description ? { description } : {}),
          input,
          detail,
        },
      });
      break;
    }

    case "question.asked": {
      if (event.properties.sessionID !== state.sessionId) {
        break;
      }

      const questions = event.properties.questions.flatMap((q) => {
        if (!q.question || !q.header) {
          return [];
        }
        const options =
          q.options?.map((o) => ({
            label: o.label,
            ...(o.description ? { description: o.description } : {}),
          })) ?? [];
        return [
          {
            question: q.question,
            header: q.header,
            options,
            ...(q.multiple === true ? { multiSelect: true } : {}),
          },
        ];
      });

      if (questions.length === 0) {
        break;
      }

      events.push({
        type: "permission_requested",
        provider: "opencode",
        request: {
          id: event.properties.id,
          provider: "opencode",
          name: "question",
          kind: "question",
          title: "Question",
          input: { questions },
          metadata: {
            source: "opencode_question",
            ...(event.properties.tool ?? {}),
          },
        },
      });
      break;
    }

    case "todo.updated": {
      if (event.properties.sessionID !== state.sessionId) {
        break;
      }

      events.push({
        type: "timeline",
        provider: "opencode",
        item: mapOpenCodeTodosToTimelineItems(event.properties.todos),
      });
      break;
    }

    case "session.compacted": {
      if (event.properties.sessionID !== state.sessionId) {
        break;
      }

      events.push({
        type: "timeline",
        provider: "opencode",
        item: createCompactionTimelineItem("completed"),
      });
      break;
    }

    case "session.idle": {
      if (event.properties.sessionID === state.sessionId) {
        state.streamedPartKeys.clear();
        state.partTypes.clear();
        events.push({
          type: "turn_completed",
          provider: "opencode",
          usage: undefined,
        });
      }
      break;
    }

    case "session.error": {
      if (event.properties.sessionID === state.sessionId) {
        state.streamedPartKeys.clear();
        state.partTypes.clear();
        events.push({
          type: "turn_failed",
          provider: "opencode",
          error: normalizeTurnFailureError(event.properties.error),
        });
      }
      break;
    }

    case "session.status": {
      if (event.properties.sessionID !== state.sessionId) {
        break;
      }
      const { status } = event.properties;
      if (status.type === "idle") {
        state.streamedPartKeys.clear();
        state.partTypes.clear();
        events.push({
          type: "turn_completed",
          provider: "opencode",
          usage: undefined,
        });
      } else if (status.type === "retry" && isFatalOpenCodeRetryMessage(status.message)) {
        state.streamedPartKeys.clear();
        state.partTypes.clear();
        events.push({
          type: "turn_failed",
          provider: "opencode",
          error: normalizeTurnFailureError(status.message),
        });
      }
      // "retry" and "busy" are transient — no terminal event.
      break;
    }
  }

  return events;
}

class OpenCodeAgentSession implements AgentSession {
  readonly provider: "opencode" = "opencode";
  readonly capabilities = OPENCODE_CAPABILITIES;

  private readonly config: OpenCodeAgentConfig;
  private readonly client: OpencodeClient;
  private readonly sessionId: string;
  private readonly logger: Logger;
  private readonly modelContextWindowsByModelKey: ReadonlyMap<string, number>;
  private currentMode: string = "default";
  private pendingPermissions = new Map<string, AgentPermissionRequest>();
  private abortController: AbortController | null = null;
  private accumulatedUsage: AgentUsage = {};
  private mcpConfigured = false;
  private mcpSetupPromise: Promise<void> | null = null;
  private allowedMcpServers: Set<string>;
  /** Tracks the role of each message by ID to distinguish user from assistant messages */
  private messageRoles = new Map<string, OpenCodeMessageRole>();
  /** Tracks streamed textual part IDs to suppress final full-text echoes from OpenCode. */
  private streamedPartKeys = new Set<string>();
  /** Tracks assistant messages already emitted from structured payloads. */
  private emittedStructuredMessageIds = new Set<string>();
  /** Tracks the type of each part by ID, learned from message.part.updated events. */
  private partTypes = new Map<string, string>();
  /** Folds native `task` subagent child-session tool parts into a live action log. */
  private readonly subAgentTracker = new OpencodeSidechainTracker();
  private availableModesCache: AgentMode[] | null = null;
  private readonly subscribers = new Set<(event: AgentStreamEvent) => void>();
  private nextTurnOrdinal = 0;
  private activeForegroundTurnId: string | null = null;
  private readonly runningToolCalls = new Map<string, ToolCallTimelineItem>();
  /**
   * Answers provided via `respondToPermission` for settled `question` tool
   * calls, keyed by `callID`.  Populated when the user answers a question so
   * the completed tool_call can render a Q→A card rather than a raw JSON dump.
   */
  private readonly questionAnswerCache = new Map<string, Record<string, string>>();
  private selectedModelContextWindowMaxTokens: number | undefined;
  constructor(
    config: OpenCodeAgentConfig,
    client: OpencodeClient,
    sessionId: string,
    logger: Logger,
    modelContextWindowsByModelKey: ReadonlyMap<string, number> = new Map(),
  ) {
    this.config = config;
    this.client = client;
    this.sessionId = sessionId;
    this.logger = logger;
    this.modelContextWindowsByModelKey = modelContextWindowsByModelKey;
    this.currentMode = normalizeOpenCodeModeId(config.modeId);
    this.selectedModelContextWindowMaxTokens = this.resolveConfiguredModelContextWindowMaxTokens(
      config.model,
    );
    this.allowedMcpServers = new Set(config.allowedMcpServers ?? []);
  }

  /** Known MCP server ids for this agent: configured servers plus the allow-list. */
  private getKnownMcpServerIds(): string[] {
    return Array.from(
      new Set([...Object.keys(this.config.mcpServers ?? {}), ...this.allowedMcpServers]),
    );
  }

  get id(): string | null {
    return this.sessionId;
  }

  async getRuntimeInfo(): Promise<AgentRuntimeInfo> {
    return {
      provider: "opencode",
      sessionId: this.sessionId,
      model: this.config.model ?? null,
      modeId: this.currentMode,
    };
  }

  async setModel(modelId: string | null): Promise<void> {
    const normalizedModelId =
      typeof modelId === "string" && modelId.trim().length > 0 ? modelId : null;
    this.config.model = normalizedModelId ?? undefined;
    this.selectedModelContextWindowMaxTokens = this.resolveConfiguredModelContextWindowMaxTokens(
      this.config.model,
    );
  }

  async setThinkingOption(thinkingOptionId: string | null): Promise<void> {
    const normalizedThinkingOptionId =
      typeof thinkingOptionId === "string" && thinkingOptionId.trim().length > 0
        ? thinkingOptionId
        : null;
    this.config.thinkingOptionId = normalizedThinkingOptionId ?? undefined;
  }

  async listMcpServers(): Promise<Array<{ name: string; status: string }>> {
    const response = await this.client.mcp.status({ directory: this.config.cwd });
    if (response.error || !response.data) {
      return [];
    }
    return Object.entries(response.data)
      .filter(([name]) => name !== "appostle")
      .map(([name, status]) => ({
        name,
        status: (status as { status?: string }).status ?? "unknown",
      }));
  }

  async connectMcpServer(name: string): Promise<void> {
    await this.runMcpOperation("connect", name, () =>
      this.client.mcp.connect({ name, directory: this.config.cwd }),
    );
    this.allowedMcpServers.add(name);
  }

  async disconnectMcpServer(name: string): Promise<void> {
    const response = await this.client.mcp.disconnect({ name, directory: this.config.cwd });
    if (response.error) {
      throw new Error(
        `Failed to disconnect MCP server '${name}': ${stringifyUnknownError(response.error)}`,
      );
    }
    this.allowedMcpServers.delete(name);
  }

  async run(prompt: AgentPromptInput, options?: AgentRunOptions): Promise<AgentRunResult> {
    const timeline: AgentTimelineItem[] = [];
    let finalText = "";
    let usage: AgentUsage | undefined;
    let turnId: string | null = null;
    const bufferedEvents: AgentStreamEvent[] = [];
    let settled = false;
    let resolveCompletion!: () => void;
    let rejectCompletion!: (error: Error) => void;

    const processEvent = (event: AgentStreamEvent) => {
      if (settled) {
        return;
      }
      const eventTurnId = (event as { turnId?: string }).turnId;
      if (turnId && eventTurnId && eventTurnId !== turnId) {
        return;
      }
      if (event.type === "timeline") {
        timeline.push(event.item);
        if (event.item.type === "assistant_message") {
          finalText = event.item.text;
        }
        return;
      }
      if (event.type === "turn_completed") {
        usage = event.usage;
        settled = true;
        resolveCompletion();
        return;
      }
      if (event.type === "turn_failed") {
        settled = true;
        rejectCompletion(new Error(event.error));
        return;
      }
      if (event.type === "turn_canceled") {
        settled = true;
        resolveCompletion();
      }
    };

    const completion = new Promise<void>((resolve, reject) => {
      resolveCompletion = resolve;
      rejectCompletion = reject;
    });
    const unsubscribe = this.subscribe((event) => {
      if (!turnId) {
        bufferedEvents.push(event);
        return;
      }
      processEvent(event);
    });

    try {
      const result = await this.startTurn(prompt, options);
      turnId = result.turnId;
      for (const event of bufferedEvents) {
        processEvent(event);
      }
      if (!settled) {
        await completion;
      }
    } finally {
      unsubscribe();
    }

    return {
      sessionId: this.sessionId,
      finalText,
      usage,
      timeline,
    };
  }

  async interrupt(): Promise<void> {
    const turnId = this.activeForegroundTurnId;
    const turnAbortController = this.abortController;
    turnAbortController?.abort();
    await this.client.session.abort({
      sessionID: this.sessionId,
      directory: this.config.cwd,
    });
    if (turnId) {
      this.finishForegroundTurn(
        { type: "turn_canceled", provider: "opencode", reason: "interrupted" },
        turnId,
      );
    }
  }

  async startTurn(
    prompt: AgentPromptInput,
    options?: AgentRunOptions,
  ): Promise<{ turnId: string }> {
    if (this.activeForegroundTurnId) {
      throw new Error("A foreground turn is already active");
    }

    this.runningToolCalls.clear();
    const turnAbortController = new AbortController();
    this.abortController = turnAbortController;
    await this.ensureMcpServersConfigured();
    const contextWindowMaxTokens = this.resolveSelectedModelContextWindowMaxTokens();
    this.accumulatedUsage = contextWindowMaxTokens !== undefined ? { contextWindowMaxTokens } : {};

    const model = this.parseModel(this.config.model);
    const thinkingOptionId = this.config.thinkingOptionId;
    const effectiveVariant = thinkingOptionId ?? undefined;
    // full-access is an Appostle-level mode (auto-approve permissions) — send "build" to OpenCode
    const effectiveMode =
      this.currentMode === "full-access" ? "build" : normalizeOpenCodeModeId(this.currentMode);

    const turnId = this.createTurnId();
    this.activeForegroundTurnId = turnId;
    void this.consumeEventStream(turnId, turnAbortController);

    const slashCommand = await this.resolveSlashCommandInvocation(prompt);
    if (slashCommand && !slashCommand.isSkill) {
      // command() blocks until the server finishes processing. OpenCode's SSE
      // endpoint does NOT replay past events, so if the command completes before
      // our SSE reader connects, we miss `session.idle` and the turn hangs.
      // Handle both success and error in the response handler as a fallback —
      // finishForegroundTurn's guard prevents duplicate terminal events if the
      // SSE stream already delivered the event.
      void this.client.session
        .command({
          sessionID: this.sessionId,
          directory: this.config.cwd,
          command: slashCommand.commandName,
          arguments: slashCommand.args ?? "",
          ...(this.config.model ? { model: this.config.model } : {}),
          ...(effectiveMode ? { agent: effectiveMode } : {}),
          ...(effectiveVariant ? { variant: effectiveVariant } : {}),
        })
        .then((response) => {
          if (response.error) {
            if (isOpenCodeHeadersTimeoutFailure(response.error)) {
              this.logger.warn(
                {
                  err: response.error,
                  commandName: slashCommand.commandName,
                  turnId,
                },
                "OpenCode slash command hit a header timeout; waiting for SSE terminal event",
              );
              return;
            }
            const errorMsg = normalizeTurnFailureError(response.error);
            this.finishForegroundTurn(
              { type: "turn_failed", provider: "opencode", error: errorMsg },
              turnId,
            );
          } else {
            this.finishForegroundTurn(
              { type: "turn_completed", provider: "opencode", usage: undefined },
              turnId,
            );
          }
        })
        .catch((err) => {
          if (isOpenCodeHeadersTimeoutFailure(err)) {
            this.logger.warn(
              {
                err,
                commandName: slashCommand.commandName,
                turnId,
              },
              "OpenCode slash command hit a header timeout; waiting for SSE terminal event",
            );
            return;
          }
          this.finishForegroundTurn(
            { type: "turn_failed", provider: "opencode", error: normalizeTurnFailureError(err) },
            turnId,
          );
        });
    } else {
      // Skill slash commands route through OpenCode's native `skill` tool
      // (progressive disclosure). Sending a skill via session.command() would
      // expand its raw SKILL.md template into the turn as if the user pasted a
      // document; a directive lets the agent load it natively, which also gives
      // it the skill's base directory so it can read the skill's resources.
      const effectivePrompt =
        slashCommand?.isSkill === true
          ? `Use the ${slashCommand.commandName} skill.${slashCommand.args ? ` ${slashCommand.args}` : ""}`
          : prompt;
      const parts = buildOpenCodePromptParts(effectivePrompt);
      void this.client.session
        .promptAsync({
          sessionID: this.sessionId,
          directory: this.config.cwd,
          parts,
          ...(options?.outputSchema
            ? {
                format: {
                  type: "json_schema" as const,
                  schema: options.outputSchema as Record<string, unknown>,
                },
              }
            : {}),
          system: composeAppostleSystemPrompt(this.config.systemPrompt),
          ...(model ? { model } : {}),
          ...(effectiveMode ? { agent: effectiveMode } : {}),
          ...(effectiveVariant ? { variant: effectiveVariant } : {}),
        })
        .then((promptResponse) => {
          if (promptResponse.error) {
            this.finishForegroundTurn(
              {
                type: "turn_failed",
                provider: "opencode",
                error: normalizeTurnFailureError(promptResponse.error),
              },
              turnId,
            );
          }
        })
        .catch((error) => {
          this.finishForegroundTurn(
            {
              type: "turn_failed",
              provider: "opencode",
              error: normalizeTurnFailureError(error),
            },
            turnId,
          );
        });
    }

    return { turnId };
  }

  subscribe(callback: (event: AgentStreamEvent) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private async consumeEventStream(
    turnId: string,
    turnAbortController: AbortController,
  ): Promise<void> {
    try {
      // OpenCode ≥ 1.14.4x stopped emitting session events on /event and moved
      // the firehose to /global/event. The legacy /event endpoint still exists
      // (and the SDK still exposes client.event.subscribe) but it stays silent
      // for session activity, which would manifest as "stream ended before
      // terminal state" after every turn. /global/event is the only path that
      // carries timeline data for the current binary.
      const result = await this.client.global.event({
        signal: turnAbortController.signal,
        sseMaxRetryAttempts: 0,
      });

      for await (const globalEvent of result.stream) {
        if (turnAbortController.signal.aborted || this.activeForegroundTurnId !== turnId) {
          break;
        }

        // /global/event is system-wide. Drop events from other workspaces.
        // Top-level system events (e.g. server.connected) have no directory
        // and should pass through untouched.
        if (globalEvent.directory && globalEvent.directory !== this.config.cwd) {
          continue;
        }

        // Each real event is re-emitted a second time wrapped as
        // {type: "sync", ...} for event-sourcing replay clients. Skip those —
        // we already handled the original.
        const payload = globalEvent.payload;
        if (payload.type === "sync") {
          continue;
        }

        const translated = this.translateEvent(payload);
        for (const e of translated) {
          if (this.activeForegroundTurnId !== turnId) {
            return;
          }
          if (e.type === "timeline" && e.item.type === "tool_call") {
            this.trackToolCall(e.item);
          }
          if (
            e.type === "turn_completed" ||
            e.type === "turn_failed" ||
            e.type === "turn_canceled"
          ) {
            if (e.type === "turn_failed") {
              this.finishForegroundTurn(
                {
                  type: "turn_failed",
                  provider: "opencode",
                  error: normalizeTurnFailureError(e.error),
                },
                turnId,
              );
            } else {
              this.finishForegroundTurn(e, turnId);
            }
            return;
          }
          this.notifySubscribers(e, turnId);
        }
      }

      if (!turnAbortController.signal.aborted && this.activeForegroundTurnId === turnId) {
        this.finishForegroundTurn(
          {
            type: "turn_failed",
            provider: "opencode",
            error: "OpenCode event stream ended before the turn reached a terminal state",
          },
          turnId,
        );
      }
    } catch (error) {
      if (!turnAbortController.signal.aborted && this.activeForegroundTurnId === turnId) {
        this.finishForegroundTurn(
          {
            type: "turn_failed",
            provider: "opencode",
            error: normalizeTurnFailureError(error),
          },
          turnId,
        );
      }
    } finally {
      if (turnAbortController.signal.aborted) {
        this.finishForegroundTurn(
          {
            type: "turn_canceled",
            provider: "opencode",
            reason: "interrupted",
          },
          turnId,
        );
      }
      if (this.abortController === turnAbortController && this.activeForegroundTurnId !== turnId) {
        this.abortController = null;
      }
    }
  }

  private finishForegroundTurn(
    event: Extract<AgentStreamEvent, { type: "turn_completed" | "turn_failed" | "turn_canceled" }>,
    turnId: string,
  ): void {
    if (this.activeForegroundTurnId !== turnId) {
      return;
    }
    if (event.type === "turn_canceled" || event.type === "turn_failed") {
      this.synthesizeInterruptedToolCalls(turnId);
    } else {
      this.runningToolCalls.clear();
    }
    this.activeForegroundTurnId = null;
    // Abort the SSE connection so the SDK tears down the underlying fetch.
    this.abortController?.abort();
    this.abortController = null;
    this.notifySubscribers(event, turnId);
  }

  private trackToolCall(item: ToolCallTimelineItem): void {
    if (item.status === "running") {
      this.runningToolCalls.set(item.callId, item);
      return;
    }
    this.runningToolCalls.delete(item.callId);
  }

  private synthesizeInterruptedToolCalls(turnId: string): void {
    for (const item of this.runningToolCalls.values()) {
      this.notifySubscribers(
        {
          type: "timeline",
          provider: "opencode",
          item: {
            ...item,
            status: "failed",
            error: { message: "Tool execution aborted" },
          },
        },
        turnId,
      );
    }
    this.runningToolCalls.clear();
  }

  private notifySubscribers(event: AgentStreamEvent, turnIdOverride?: string): void {
    const turnId = turnIdOverride ?? this.activeForegroundTurnId;
    const tagged = turnId ? { ...event, turnId } : event;
    for (const callback of this.subscribers) {
      try {
        callback(tagged);
      } catch {
        // Subscriber callback error isolation
      }
    }
  }

  private createTurnId(): string {
    return `opencode-turn-${this.nextTurnOrdinal++}`;
  }

  async *streamHistory(): AsyncGenerator<AgentStreamEvent> {
    const response = await this.client.session.messages({
      sessionID: this.sessionId,
      directory: this.config.cwd,
    });

    if (response.error || !response.data) {
      return;
    }

    for (const { info, parts } of response.data) {
      if (info.role === "user") {
        const text = parts
          .filter((p): p is Extract<OpenCodePart, { type: "text" }> => p.type === "text")
          .map((p) => p.text)
          .join("");

        if (text) {
          yield {
            type: "timeline",
            provider: "opencode",
            item: { type: "user_message", text },
          };
        }
      } else {
        let emittedAssistantText = false;
        for (const part of parts) {
          if (part.type === "text") {
            if (part.text) {
              emittedAssistantText = true;
              yield {
                type: "timeline",
                provider: "opencode",
                item: { type: "assistant_message", text: part.text },
              };
            }
          } else if (part.type === "reasoning") {
            if (part.text) {
              yield {
                type: "timeline",
                provider: "opencode",
                item: { type: "reasoning", text: part.text },
              };
            }
          } else if (part.type === "tool") {
            const toolItem = mapOpencodeToolPartToTimelineItem(
              part,
              this.getKnownMcpServerIds(),
              this.questionAnswerCache,
            );
            if (toolItem) {
              yield {
                type: "timeline",
                provider: "opencode",
                item: toolItem,
              };
            }
          }
        }

        if (!emittedAssistantText) {
          const text = stringifyStructuredAssistantMessage(info.structured);
          if (text) {
            yield {
              type: "timeline",
              provider: "opencode",
              item: { type: "assistant_message", text },
            };
          }
        }
      }
    }
  }

  async getAvailableModes(): Promise<AgentMode[]> {
    if (this.availableModesCache) {
      return this.availableModesCache;
    }

    const response = await this.client.app.agents({
      directory: this.config.cwd,
    });

    const discoveredModes =
      response.error || !response.data
        ? []
        : response.data
            .filter((agent) => agent.mode === "primary" && agent.hidden !== true)
            .map((agent) => ({
              id: agent.name,
              label: agent.name.charAt(0).toUpperCase() + agent.name.slice(1),
              description:
                typeof agent.description === "string" && agent.description.trim().length > 0
                  ? agent.description.trim()
                  : DEFAULT_MODES.find((mode) => mode.id === agent.name)?.description,
            }));

    const modes = discoveredModes.length > 0 ? sortOpenCodeModes(discoveredModes) : DEFAULT_MODES;
    const fullAccess = DEFAULT_MODES.find((m) => m.id === "full-access")!;
    if (!modes.some((m) => m.id === "full-access")) {
      modes.splice(1, 0, fullAccess);
    }
    this.availableModesCache = modes;
    return this.availableModesCache;
  }

  async getCurrentMode(): Promise<string | null> {
    return this.currentMode;
  }

  async listCommands(): Promise<AgentSlashCommand[]> {
    const result = await this.client.command.list({
      directory: this.config.cwd,
    });
    const nativeCommands: AgentSlashCommand[] =
      result.error || !result.data
        ? []
        : result.data.map((cmd) => ({
            name: cmd.name,
            description: cmd.description ?? "",
            argumentHint: cmd.hints?.length ? cmd.hints.join(" ") : "",
          }));

    // OpenCode's command discovery doesn't see per-project `.claude/skills`.
    // Merge those in so they surface in the composer; native commands win on
    // a name collision.
    const projectSkills = await scanProjectClaudeSkills(this.config.cwd);
    const byName = new Map<string, AgentSlashCommand>();
    for (const skill of projectSkills) {
      byName.set(skill.name, {
        name: skill.name,
        description: skill.description,
        argumentHint: "",
      });
    }
    for (const cmd of nativeCommands) {
      byName.set(cmd.name, cmd);
    }
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  async setMode(modeId: string): Promise<void> {
    this.currentMode = normalizeOpenCodeModeId(modeId);
  }

  getPendingPermissions(): AgentPermissionRequest[] {
    return Array.from(this.pendingPermissions.values());
  }

  async respondToPermission(requestId: string, response: AgentPermissionResponse): Promise<void> {
    const pending = this.pendingPermissions.get(requestId);
    if (!pending) {
      throw new Error(`No pending permission request with id '${requestId}'`);
    }

    if (pending.kind === "question") {
      if (response.behavior === "deny") {
        await this.client.question.reject({
          requestID: requestId,
          directory: this.config.cwd,
        });
      } else {
        const answersRecord = readOpenCodeRecord(response.updatedInput?.answers);
        const questions = Array.isArray(pending.input?.questions) ? pending.input.questions : [];
        const answers = questions.map((item) => {
          // QuestionFormCard keys the answers record by question text (`q.question`),
          // not by header — look it up the same way or the answer is dropped and
          // OpenCode receives "Unanswered".
          const questionText = readNonEmptyString(readOpenCodeRecord(item)?.question);
          const rawAnswer = questionText ? readNonEmptyString(answersRecord?.[questionText]) : null;
          if (!rawAnswer) {
            return [];
          }
          return rawAnswer
            .split(",")
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0);
        });

        await this.client.question.reply({
          requestID: requestId,
          directory: this.config.cwd,
          answers,
        });

        // Cache the answers so the settled `question` tool_call can render a
        // Q→A card. The `updatedInput.answers` record is keyed by question text
        // (built by QuestionFormCard) — that's exactly what
        // AskUserQuestionAnswersCard expects, so we store it verbatim.
        const callId =
          typeof pending.metadata?.callID === "string" ? pending.metadata.callID : null;
        const answersForCard = readOpenCodeRecord(response.updatedInput?.answers);
        if (callId && answersForCard) {
          this.questionAnswerCache.set(callId, answersForCard as Record<string, string>);
        }
      }

      this.pendingPermissions.delete(requestId);
      return;
    }

    const reply = response.behavior === "allow" ? "once" : "reject";
    await this.client.permission.reply({
      requestID: requestId,
      directory: this.config.cwd,
      reply,
      message: response.behavior === "deny" ? response.message : undefined,
    });

    this.pendingPermissions.delete(requestId);
  }

  describePersistence(): AgentPersistenceHandle | null {
    return {
      provider: "opencode",
      sessionId: this.sessionId,
      nativeHandle: this.sessionId,
      metadata: {
        cwd: this.config.cwd,
      },
    };
  }

  async close(): Promise<void> {
    this.abortController?.abort();
    await reconcileOpenCodeSessionClose({
      client: this.client,
      sessionId: this.sessionId,
      directory: this.config.cwd,
      logger: this.logger,
    });
    this.subscribers.clear();
    this.activeForegroundTurnId = null;
  }

  private parseSlashCommandInput(text: string): { commandName: string; args?: string } | null {
    const trimmed = text.trim();
    if (!trimmed.startsWith("/") || trimmed.length <= 1) {
      return null;
    }
    const withoutPrefix = trimmed.slice(1);
    const firstWhitespaceIdx = withoutPrefix.search(/\s/);
    const commandName =
      firstWhitespaceIdx === -1 ? withoutPrefix : withoutPrefix.slice(0, firstWhitespaceIdx);
    if (!commandName || commandName.includes("/")) {
      return null;
    }
    const rawArgs =
      firstWhitespaceIdx === -1 ? "" : withoutPrefix.slice(firstWhitespaceIdx + 1).trim();
    return rawArgs.length > 0 ? { commandName, args: rawArgs } : { commandName };
  }

  private async resolveSlashCommandInvocation(
    prompt: AgentPromptInput,
  ): Promise<{ commandName: string; args?: string; isSkill: boolean } | null> {
    if (typeof prompt !== "string") {
      return null;
    }
    const parsed = this.parseSlashCommandInput(prompt);
    if (!parsed) {
      return null;
    }
    try {
      const result = await this.client.command.list({
        directory: this.config.cwd,
      });
      if (result.error || !result.data) {
        return null;
      }
      const match = result.data.find((command) => command.name === parsed.commandName);
      if (!match) {
        return null;
      }
      return { ...parsed, isSkill: match.source === "skill" };
    } catch (error) {
      this.logger.warn(
        { err: error, commandName: parsed.commandName },
        "Failed to resolve slash command; falling back to plain prompt input",
      );
      return null;
    }
  }

  private parseModel(model?: string): { providerID: string; modelID: string } | undefined {
    if (!model) {
      return undefined;
    }
    const parts = model.split("/");
    if (parts.length >= 2) {
      return { providerID: parts[0], modelID: parts.slice(1).join("/") };
    }
    return { providerID: "opencode", modelID: model };
  }

  private async ensureMcpServersConfigured(): Promise<void> {
    if (this.mcpConfigured) {
      return;
    }

    const mcpServers = this.config.mcpServers;
    if (!mcpServers || Object.keys(mcpServers).length === 0) {
      this.mcpConfigured = true;
      return;
    }

    if (!this.mcpSetupPromise) {
      this.mcpSetupPromise = this.configureMcpServers(mcpServers);
    }

    try {
      await this.mcpSetupPromise;
      this.mcpConfigured = true;
    } catch (error) {
      this.mcpSetupPromise = null;
      throw error;
    }
  }

  private async configureMcpServers(mcpServers: Record<string, McpServerConfig>): Promise<void> {
    for (const [name, serverConfig] of Object.entries(mcpServers)) {
      // appostle server starts enabled; all other user servers start disabled by default.
      const isAppostle = name === "appostle";
      const mappedConfig = toOpenCodeMcpConfig(serverConfig, isAppostle);
      if (!mappedConfig) continue;
      await this.registerMcpServer(name, mappedConfig);
    }
    // Connect any user servers that are in the allowedMcpServers set for this agent.
    for (const name of this.allowedMcpServers) {
      if (name !== "appostle" && mcpServers[name]) {
        await this.runMcpOperation("connect", name, () =>
          this.client.mcp.connect({ name, directory: this.config.cwd }),
        );
      }
    }
  }

  private async registerMcpServer(name: string, config: OpenCodeMcpConfig): Promise<void> {
    // `mcp.add` already connects the server inline (its response reports
    // status "connected"). A separate `mcp.connect` call looks the server up in
    // a registry that `add` does not populate for runtime-added servers, so it
    // fails with McpServerNotFoundError and kills the turn. Add is sufficient.
    await this.runMcpOperation("add", name, () =>
      this.client.mcp.add({
        directory: this.config.cwd,
        name,
        config,
      }),
    );
  }

  private async runMcpOperation(
    operation: "add" | "connect",
    name: string,
    run: () => Promise<{ error?: unknown }>,
  ): Promise<void> {
    const response = await run();
    const error = response.error;
    if (!error) {
      return;
    }

    if (isAlreadyPresentMcpError(error)) {
      return;
    }

    throw new Error(
      `Failed to ${operation} OpenCode MCP server '${name}': ${stringifyUnknownError(error)}`,
    );
  }

  private translateEvent(event: OpenCodeEvent): AgentStreamEvent[] {
    const translated = translateOpenCodeEvent(event, {
      sessionId: this.sessionId,
      messageRoles: this.messageRoles,
      accumulatedUsage: this.accumulatedUsage,
      streamedPartKeys: this.streamedPartKeys,
      emittedStructuredMessageIds: this.emittedStructuredMessageIds,
      partTypes: this.partTypes,
      mcpServerIds: this.getKnownMcpServerIds(),
      subAgentTracker: this.subAgentTracker,
      questionAnswerCache: this.questionAnswerCache,
      modelContextWindowsByModelKey: this.modelContextWindowsByModelKey,
      onAssistantModelContextWindowResolved: (contextWindowMaxTokens) => {
        this.accumulatedUsage.contextWindowMaxTokens = contextWindowMaxTokens;
        if (!this.config.model) {
          this.selectedModelContextWindowMaxTokens = contextWindowMaxTokens;
        }
      },
    });

    const isFullAccess = this.currentMode === "full-access";
    const filtered: AgentStreamEvent[] = [];

    for (const translatedEvent of translated) {
      // Full Access auto-approves tool permission *gates* so the agent runs
      // without prompts. It must NOT auto-answer `question` permissions —
      // those are deliberate asks directed at the user, not a gate to clear.
      // Auto-replying them with empty answers delivered "Unanswered" to the
      // agent, silently dismissing every question. Let questions fall through
      // to the normal pending-permission path so the user can answer them.
      if (
        translatedEvent.type === "permission_requested" &&
        isFullAccess &&
        translatedEvent.request.kind !== "question"
      ) {
        void this.client.permission.reply({
          requestID: translatedEvent.request.id,
          directory: this.config.cwd,
          reply: "once",
        });
        continue;
      }
      if (translatedEvent.type === "permission_requested") {
        this.pendingPermissions.set(translatedEvent.request.id, translatedEvent.request);
      }
      if (translatedEvent.type === "turn_completed") {
        if (hasNormalizedOpenCodeUsage(this.accumulatedUsage)) {
          translatedEvent.usage = this.accumulatedUsage;
        }
        const contextWindowMaxTokens = this.resolveSelectedModelContextWindowMaxTokens();
        this.accumulatedUsage =
          contextWindowMaxTokens !== undefined ? { contextWindowMaxTokens } : {};
      }
      filtered.push(translatedEvent);
    }

    return filtered;
  }

  private resolveSelectedModelContextWindowMaxTokens(): number | undefined {
    return this.selectedModelContextWindowMaxTokens;
  }

  private resolveConfiguredModelContextWindowMaxTokens(
    modelId: string | undefined,
  ): number | undefined {
    const modelLookupKey = parseOpenCodeModelLookupKey(modelId);
    if (!modelLookupKey) {
      return undefined;
    }
    return this.modelContextWindowsByModelKey.get(modelLookupKey);
  }
}
